import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  BarChart3, 
  BookOpen, 
  Brain, 
  CheckCircle, 
  Crown, 
  FileText, 
  GraduationCap, 
  LineChart, 
  MessageSquare, 
  School, 
  Target, 
  TrendingUp 
} from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const FeaturesPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9F7FE] to-[#EADFFF] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Powerful Features for <br />
              <span className="text-[#7B4AE2]">Smart College Selection</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Everything you need to make informed decisions about your 11th-grade college admissions
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
            >
              Get Started Free
              <GraduationCap className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Comprehensive Feature Set</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform offers everything you need to find and get admitted to the perfect college
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* AI Predictions */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#F0E9FF] rounded-lg flex items-center justify-center mb-4">
                <Brain className="h-6 w-6 text-[#7B4AE2]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI-Powered Predictions</h3>
              <p className="text-gray-600">
                Get accurate predictions for cutoff marks and admission chances using our advanced AI algorithms
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  95% prediction accuracy
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Based on 10+ years of data
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Real-time updates
                </li>
              </ul>
            </div>

            {/* College Comparison */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#E0F9F9] rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-[#18CDCA]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">College Comparison</h3>
              <p className="text-gray-600">
                Compare up to 10 colleges side by side with detailed insights and analytics
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Side-by-side comparison
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Multiple parameters
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Export comparison data
                </li>
              </ul>
            </div>

            {/* Cutoff Analysis */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#FFF5D9] rounded-lg flex items-center justify-center mb-4">
                <LineChart className="h-6 w-6 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Cutoff Analysis</h3>
              <p className="text-gray-600">
                Track historical cutoff trends and get round-wise predictions for each college
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Historical data analysis
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Round-wise predictions
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Category-wise insights
                </li>
              </ul>
            </div>

            {/* Personalized Dashboard */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#FFE9ED] rounded-lg flex items-center justify-center mb-4">
                <Target className="h-6 w-6 text-[#EF476F]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Personalized Dashboard</h3>
              <p className="text-gray-600">
                Get a customized dashboard with colleges that match your profile and preferences
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Profile-based matching
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Custom recommendations
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Progress tracking
                </li>
              </ul>
            </div>

            {/* AI Chat Assistant */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#F0E9FF] rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-[#7B4AE2]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">AI Chat Assistant</h3>
              <p className="text-gray-600">
                Get instant answers to your college-related queries from our AI assistant
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  24/7 availability
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Accurate responses
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Personalized guidance
                </li>
              </ul>
            </div>

            {/* Document Processing */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#E0F9F9] rounded-lg flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-[#18CDCA]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Document Processing</h3>
              <p className="text-gray-600">
                Automatic processing of marksheets and academic documents for accurate predictions
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  OCR technology
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Secure storage
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Quick verification
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features */}
      <section className="bg-[#F9F7FE] py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-[#F0E9FF] px-4 py-2 rounded-full text-[#7B4AE2] font-medium mb-4">
              <Crown className="h-5 w-5 mr-2" />
              Premium Features
            </div>
            <h2 className="text-3xl font-bold mb-4">Unlock Advanced Features</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get access to premium features that give you a competitive edge in college admissions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6">
              <TrendingUp className="h-8 w-8 text-[#7B4AE2] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Advanced Analytics</h3>
              <p className="text-gray-600">
                Deep insights into admission trends and patterns
              </p>
            </div>

            <div className="bg-white rounded-lg p-6">
              <School className="h-8 w-8 text-[#7B4AE2] mb-4" />
              <h3 className="text-xl font-semibold mb-2">College Insights</h3>
              <p className="text-gray-600">
                Detailed reports on college infrastructure and facilities
              </p>
            </div>

            <div className="bg-white rounded-lg p-6">
              <BookOpen className="h-8 w-8 text-[#7B4AE2] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Success Stories</h3>
              <p className="text-gray-600">
                Access to admission success stories and strategies
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/pricing" 
              className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
            >
              View Pricing
              <Crown className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Find Your Perfect College?
            </h2>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
              Join thousands of students who have found their ideal college match using our platform
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-white text-[#7B4AE2] rounded-lg hover:bg-opacity-90 transition-colors font-medium"
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;